// ═══════════════════════════════════════════════════════════════
// V·IANet Flow — ContextMenu: right-click context menu
// Node / Edge / Canvas actions
// ═══════════════════════════════════════════════════════════════

import { useEffect, useRef } from 'react';
import { FLOW_TOKENS as T } from '../shared/flowTokens';

// ─── Types ───────────────────────────────────────────────────────────────────

export type ContextMenuType = 'node' | 'edge' | 'canvas';

export type ContextMenuAction =
  | 'duplicate'
  | 'bringToFront'
  | 'sendToBack'
  | 'delete'
  | 'editLabel'
  | 'changeStyleSolid'
  | 'changeStyleDashed'
  | 'changeStyleDotted'
  | 'changeArrowArrow'
  | 'changeArrowNone'
  | 'changeArrowBidirectional'
  | 'changeArrowOpen'
  | 'paste'
  | 'fitView'
  | 'autoLayout';

interface ContextMenuProps {
  x: number;
  y: number;
  type: ContextMenuType;
  onAction: (action: ContextMenuAction) => void;
  onClose: () => void;
}

// ─── Menu Item ──────────────────────────────────────────────────────────────

function MenuItem({
  label,
  action,
  onAction,
  danger,
}: {
  label: string;
  action: ContextMenuAction;
  onAction: (a: ContextMenuAction) => void;
  danger?: boolean;
}) {
  return (
    <button
      onClick={() => onAction(action)}
      style={{
        display: 'block',
        width: '100%',
        padding: '5px 14px',
        border: 'none',
        background: 'none',
        textAlign: 'left',
        fontSize: '12px',
        fontWeight: 500,
        fontFamily: T.font.family,
        color: danger ? '#DC2626' : T.color.textPrimary,
        cursor: 'pointer',
        whiteSpace: 'nowrap',
      }}
      onMouseEnter={(e) => {
        (e.currentTarget as HTMLElement).style.backgroundColor = T.color.surfaceSubtle;
      }}
      onMouseLeave={(e) => {
        (e.currentTarget as HTMLElement).style.backgroundColor = 'transparent';
      }}
    >
      {label}
    </button>
  );
}

function MenuDivider() {
  return (
    <div
      style={{
        height: 1,
        backgroundColor: T.color.border,
        margin: '4px 0',
      }}
    />
  );
}

// ─── Submenu for edge styles ────────────────────────────────────────────────

function SubMenu({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={{ position: 'relative' }}>
      <div
        style={{
          padding: '5px 14px',
          fontSize: '12px',
          fontWeight: 500,
          fontFamily: T.font.family,
          color: T.color.textPrimary,
          cursor: 'default',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 8,
        }}
      >
        {label}
        <svg
          width={8}
          height={8}
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth={2}
        >
          <path d="M9 6l6 6-6 6" />
        </svg>
      </div>
      <div
        style={{
          paddingLeft: 10,
          borderLeft: `2px solid ${T.color.border}`,
          marginLeft: 10,
        }}
      >
        {children}
      </div>
    </div>
  );
}

// ─── Component ──────────────────────────────────────────────────────────────

export default function ContextMenu({ x, y, type, onAction, onClose }: ContextMenuProps) {
  const ref = useRef<HTMLDivElement>(null);

  // Close on Escape or click outside
  useEffect(() => {
    function handleKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as HTMLElement)) {
        onClose();
      }
    }
    document.addEventListener('keydown', handleKey);
    document.addEventListener('mousedown', handleClick);
    return () => {
      document.removeEventListener('keydown', handleKey);
      document.removeEventListener('mousedown', handleClick);
    };
  }, [onClose]);

  // Wrapped onAction that also closes the menu
  const act = (action: ContextMenuAction) => {
    onAction(action);
    onClose();
  };

  return (
    <div
      ref={ref}
      style={{
        position: 'fixed',
        left: x,
        top: y,
        zIndex: 9999,
        backgroundColor: '#FFFFFF',
        borderRadius: 7,
        boxShadow: '0 4px 16px rgba(0,0,0,0.15)',
        padding: '7px 0',
        minWidth: 150,
      }}
    >
      {type === 'node' && (
        <>
          <MenuItem label="Duplicar" action="duplicate" onAction={act} />
          <MenuItem label="Traer al frente" action="bringToFront" onAction={act} />
          <MenuItem label="Enviar al fondo" action="sendToBack" onAction={act} />
          <MenuDivider />
          <MenuItem label="Eliminar" action="delete" onAction={act} danger />
        </>
      )}

      {type === 'edge' && (
        <>
          <MenuItem label="Editar etiqueta" action="editLabel" onAction={act} />
          <SubMenu label="Estilo de linea">
            <MenuItem label="Solida" action="changeStyleSolid" onAction={act} />
            <MenuItem label="Discontinua" action="changeStyleDashed" onAction={act} />
            <MenuItem label="Punteada" action="changeStyleDotted" onAction={act} />
          </SubMenu>
          <SubMenu label="Tipo de flecha">
            <MenuItem label="Flecha" action="changeArrowArrow" onAction={act} />
            <MenuItem label="Sin flecha" action="changeArrowNone" onAction={act} />
            <MenuItem label="Bidireccional" action="changeArrowBidirectional" onAction={act} />
            <MenuItem label="Abierta" action="changeArrowOpen" onAction={act} />
          </SubMenu>
          <MenuDivider />
          <MenuItem label="Eliminar" action="delete" onAction={act} danger />
        </>
      )}

      {type === 'canvas' && (
        <>
          <MenuItem label="Pegar" action="paste" onAction={act} />
          <MenuItem label="Ajustar vista" action="fitView" onAction={act} />
          <MenuItem label="Auto-layout" action="autoLayout" onAction={act} />
        </>
      )}
    </div>
  );
}
